import {Asset} from '@contentlab/types/asset';
import {FlowStatus, FeatureName} from '@contentlab/types/project';
import {AssetProps} from '@contentlab/types/projectProps';
import processAssetErrorCodes from '@contentlab/utils/processAssetErrorCodes';

/**
 * Maps an Asset to AssetProps
 * @param asset - The asset to map
 * @param flowId - The flow ID (optional)
 * @param type - The asset type (optional)
 * @param errorCodes - Error codes array (optional)
 * @param feature - Feature name for context-specific error messages (optional)
 * @returns AssetProps object
 */
export default function mapAssetToAssetProps(
  asset: Asset,
  flowId?: string | null,
  type?: 'inputAsset' | 'outputAsset',
  errorCodes?: string[],
  feature?: FeatureName | string
): AssetProps {
  // Convert string status to FlowStatus
  const getFlowStatus = (status: string): FlowStatus => {
    switch (status.toLowerCase()) {
      case 'ready':
        return FlowStatus.READY;
      case 'in_progress':
        return FlowStatus.IN_PROGRESS;
      case 'failed':
        return FlowStatus.FAILED;
      case 'pending':
        return FlowStatus.PENDING;
      case 'pending_user_action':
        return FlowStatus.PENDING_USER_ACTION;
      default:
        return FlowStatus.PENDING;
    }
  };

  return {
    id: asset.id,
    name: asset.name,
    isArchived: false, // Default value since Asset doesn't have this property
    metadata: asset.metadata,
    compositeId: flowId ? `${flowId}-${asset.id}` : asset.id,
    flowId: flowId || asset.id,
    thumbnailUrl: asset.thumbnailUrl,
    url: asset.url,
    type,
    status: getFlowStatus(asset.status),
    errors: processAssetErrorCodes(errorCodes, feature),
    createdAt: asset.createdAt ? new Date(asset.createdAt) : new Date(),
    userInput: null, // Default value since Asset doesn't have userInput
    userInputVersion: 1, // Default value since Asset doesn't have userInputVersion
    progressPercentage: null, // Default value since Asset doesn't have this property
    estimatedCompletionTime: null, // Default value since Asset doesn't have this property
  };
}
